import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Typography } from 'antd';
import { getMemberTagEvidence } from '@/services/technical';
import { readTagEvidence } from './MemberTagSupport';

const { Paragraph, Text } = Typography;

export default function MemberTagEvidence({
  memberId,
  observationId,
  expectedKind = 'QUESTIONNAIRE_ANSWER',
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const generation = useRef(0);
  useEffect(() => {
    generation.current += 1;
    setData(null);
    setError('');
    setLoading(false);
    return () => {
      generation.current += 1;
    };
  }, [memberId, observationId, expectedKind]);

  const load = async () => {
    const current = ++generation.current;
    setLoading(true);
    setError('');
    setData(null);
    try {
      const response = await getMemberTagEvidence({
        requestData: { memberId, observationId },
      });
      if (current === generation.current)
        setData(
          readTagEvidence(response, memberId, observationId, expectedKind),
        );
    } catch (failure) {
      if (current !== generation.current) return;
      const status = failure?.response?.status;
      setError(
        status === 403
          ? '无会员档案菜单权限'
          : status === 401
          ? '请先登录后台'
          : '引用证据暂时无法加载，请重试',
      );
    } finally {
      if (current === generation.current) setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: 12 }}>
      <Button size="small" disabled={loading} onClick={load}>
        {loading ? '加载引用证据中…' : error ? '重试引用证据' : '查看引用证据'}
      </Button>
      {error && <Paragraph type="warning">{error}</Paragraph>}
      {data?.status === 'AVAILABLE' ? (
        <div style={{ marginTop: 8, maxHeight: 320, overflowY: 'auto' }}>
          <Paragraph type="secondary">
            {data.evidenceKind === 'COURSE_SUMMARY'
              ? '以下为提取标签时引用的课堂摘要片段，可能经过截取，不是逐字转写，不能据此确认说话人。'
              : data.evidenceKind === 'CHAT_MESSAGE'
              ? '以下是提取标签时引用的消息片段，可能已经截取，不是完整群聊上下文。'
              : '以下为该标签引用的冻结问卷记录，不代表当前情况已确认。'}
          </Paragraph>
          {data.items.map((item, index) => (
            <div key={`${item.ref}:${index}`}>
              <Text strong>{item.question}</Text>
              <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                {item.answer}
              </Paragraph>
            </div>
          ))}
        </div>
      ) : data ? (
        <Paragraph type="secondary">
          {data.status === 'UNSUPPORTED'
            ? '该来源暂未支持原文展示'
            : '引用证据缺失或格式不可用，未展示原文'}
        </Paragraph>
      ) : null}
    </div>
  );
}

MemberTagEvidence.propTypes = {
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  observationId: PropTypes.string.isRequired,
  expectedKind: PropTypes.oneOf([
    'QUESTIONNAIRE_ANSWER',
    'COURSE_SUMMARY',
    'CHAT_MESSAGE',
  ]),
};
